import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';
import { createSlug } from '../../../util/urlHelpers';

import css from './PriceVariantPicker.module.css';

const { Money } = sdkTypes;

const DEFAULT_PRICE_VARIANT_NAME = 'default-variant-name';

const VariantNameMaybe = props => {
  const { className, priceVariant } = props;
  return priceVariant?.name ? (
    <div className={className}>
      <FormattedMessage
        id="PriceVariantPicker.onePriceVariantOnly"
        values={{ priceVariantName: priceVariant?.name }}
      />
    </div>
  ) : null;
};

const FieldHidden = props => {
  const { name, ...rest } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.hidden} {...rest}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

// A price variant in publicData is { name, priceInSubunits } — it carries NO currency: neither
// the listing wizard (EditListingPricingPanel/BookingPriceVariants) nor the space importers write
// one. The currency therefore comes from the marketplace config; OrderPanel refuses listings whose
// price currency differs from it, so the two always agree for a rendered panel.
const formatVariantPrice = (priceVariant, currency, intl) => {
  const { priceInSubunits } = priceVariant || {};
  if (priceInSubunits == null || !currency) return null;
  try {
    // formatMoney throws on anything that is not a Money instance — a plain {amount, currency}
    // object is silently swallowed by the catch below and the tile loses its price.
    return formatMoney(intl, new Money(priceInSubunits, currency));
  } catch (e) {
    return null;
  }
};

const PriceVariantPicker = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const { priceVariants, onPriceVariantChange, disabled, marketplaceCurrency } = props;
  const currency = marketplaceCurrency || config?.currency;
  const hasMultiplePriceVariants = priceVariants?.length > 1;
  const hasOnePriceVariant = priceVariants?.length === 1;

  if (hasMultiplePriceVariants) {
    const label = intl.formatMessage({ id: 'PriceVariantPicker.priceVariantLabel' });
    return (
      <div className={css.priceVariantFieldSelect}>
        <label className={css.tilesLabel}>{label}</label>
        <Field name="priceVariantName">
          {({ input, meta }) => (
            <>
              <div role="radiogroup" aria-label={label} className={css.tiles} data-slug-container>
                {priceVariants.map(pv => {
                  const value = pv.name;
                  const selected = input.value === value;
                  const priceLabel = formatVariantPrice(pv, pv.currency || currency, intl);
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={disabled}
                      data-slug={createSlug(value)}
                      className={classNames(css.tile, {
                        [css.tileSelected]: selected,
                        [css.tileDisabled]: disabled,
                      })}
                      onClick={() => {
                        if (disabled) return;
                        input.onChange(value);
                        if (typeof onPriceVariantChange === 'function') {
                          onPriceVariantChange(value);
                        }
                      }}
                    >
                      <span className={css.tileName}>{pv.name}</span>
                      {priceLabel ? <span className={css.tilePrice}>{priceLabel}</span> : null}
                    </button>
                  );
                })}
              </div>
              {meta?.touched && meta?.error ? <div className={css.error}>{meta.error}</div> : null}
            </>
          )}
        </Field>
      </div>
    );
  }

  return hasOnePriceVariant ? (
    <>
      <VariantNameMaybe priceVariant={priceVariants?.[0]} className={css.priceVariantName} />
      <FieldHidden
        name="priceVariantName"
        format={value => {
          return value == null ? DEFAULT_PRICE_VARIANT_NAME : value;
        }}
        parse={value => {
          const response = value === DEFAULT_PRICE_VARIANT_NAME ? null : value;
          return response;
        }}
      />
    </>
  ) : null;
};

export default PriceVariantPicker;
